import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Calendar, ArrowRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "../lib/api";
import useSEO from "../hooks/useSEO";
import BlogBlocks, { mdComponents } from "../components/BlogBlocks";

const RUBRIQUES = [
  { slug: "", label: "Tout" },
  { slug: "événements", label: "Événements", aliases: ["evenements", "événements", "evénements"] },
  { slug: "entrepreneuriat", label: "Entrepreneuriat", aliases: ["entrepreneuriat"] },
  { slug: "immigration", label: "Immigration", aliases: ["immigration", "établissement", "etablissement"] },
  { slug: "témoignages", label: "Témoignages", aliases: ["temoignages", "témoignages"] },
];

function normalize(s) {
  return (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export function BlogList() {
  useSEO({ title: "Demka Média - Blog", description: "Actualités, conseils et ressources pour les nouveaux arrivants et entrepreneurs francophones au Canada." });
  const [posts, setPosts] = useState([]);
  const [params, setParams] = useSearchParams();
  const active = params.get("rubrique") || "";

  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)); }, []);

  const filtered = useMemo(() => {
    if (!active) return posts;
    const rub = RUBRIQUES.find((r) => r.slug === active);
    const aliases = (rub?.aliases || [active]).map(normalize);
    return posts.filter((p) => aliases.includes(normalize(p.category)));
  }, [posts, active]);

  const counts = useMemo(() => {
    const c = {};
    for (const r of RUBRIQUES) {
      if (!r.slug) { c[""] = posts.length; continue; }
      const aliases = (r.aliases || [r.slug]).map(normalize);
      c[r.slug] = posts.filter((p) => aliases.includes(normalize(p.category))).length;
    }
    return c;
  }, [posts]);

  const setRubrique = (slug) => {
    const p = new URLSearchParams(params);
    if (!slug) p.delete("rubrique"); else p.set("rubrique", slug);
    setParams(p, { replace: true });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="blog-list-page">
      <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-2">Demka Média</p>
      <h1 className="font-display font-bold text-4xl sm:text-5xl text-foreground tracking-tight">Le blog DemkaBiz</h1>
      <p className="text-muted-foreground mt-2">Actualités, conseils et ressources pour les nouveaux arrivants et entrepreneurs.</p>

      {/* Rubriques filter */}
      <div className="flex flex-wrap gap-2 mt-8" data-testid="blog-rubriques">
        {RUBRIQUES.map((r) => (
          <button
            key={r.slug || "all"}
            onClick={() => setRubrique(r.slug)}
            data-testid={`rubrique-${r.slug || "all"}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition ${
              active === r.slug
                ? "bg-primary text-primary-foreground shadow-sm"
                : "bg-white border border-primary/15 text-foreground/70 hover:bg-secondary hover:text-foreground"
            }`}
          >
            {r.label}
            <span className={`text-xs font-normal ${active === r.slug ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
              {counts[r.slug] ?? 0}
            </span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
        {filtered.map((p) => (
          <Link key={p.id} to={`/blog/${p.slug || p.id}`} data-testid={`blog-card-${p.id}`} className="card-surface card-hover overflow-hidden group">
            {p.cover_url && (
              <div className="aspect-[2/1] bg-secondary overflow-hidden flex items-center justify-center">
                <img src={p.cover_url} alt={p.title} className="w-full h-full object-contain group-hover:scale-[1.03] transition-transform duration-700" loading="lazy" />
              </div>
            )}
            <div className="p-6">
              <span className="text-xs uppercase tracking-wider text-primary font-semibold">{p.category}</span>
              <h3 className="font-display font-semibold text-xl mt-1 leading-tight">{p.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{p.excerpt}</p>
              <span className="inline-flex items-center gap-1 text-sm text-primary mt-4 group-hover:gap-2 transition-all">
                Lire <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>
        ))}
      </div>
      {filtered.length === 0 && (
        <div className="text-center py-16 text-muted-foreground border border-dashed border-primary/20 rounded-2xl mt-6" data-testid="blog-empty">
          Aucun article {active ? "dans cette rubrique" : "publié"} pour le moment.
        </div>
      )}
    </div>
  );
}

export function BlogPost() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [adRect, setAdRect] = useState(null);
  const [adHoriz, setAdHoriz] = useState(null);
  useEffect(() => {
    api.get(`/blog/${id}`).then((r) => setPost(r.data));
    api.get("/ads/blog_rectangle").then((r) => setAdRect(r.data?.placement ? r.data : null)).catch(() => {});
    api.get("/ads/blog_horizontal").then((r) => setAdHoriz(r.data?.placement ? r.data : null)).catch(() => {});
  }, [id]);
  useSEO({ title: post?.title || "Article", description: post?.excerpt });
  if (!post) return <div className="p-12 text-center text-muted-foreground">Chargement…</div>;
  const AdRect = adRect && adRect.image_url ? (
    <a href={adRect.target_url || "#"} target={adRect.target_url ? "_blank" : undefined} rel="noreferrer" className="block my-8 mx-auto max-w-sm rounded-2xl overflow-hidden bg-secondary" data-testid="blog-ad-rectangle">
      <img src={adRect.image_url} alt={adRect.title || "Publicité"} className="w-full h-auto object-cover" />
    </a>
  ) : null;
  const AdHoriz = adHoriz && adHoriz.image_url ? (
    <a href={adHoriz.target_url || "#"} target={adHoriz.target_url ? "_blank" : undefined} rel="noreferrer" className="block my-10 rounded-2xl overflow-hidden bg-secondary" data-testid="blog-ad-horizontal">
      <img src={adHoriz.image_url} alt={adHoriz.title || "Publicité"} className="w-full h-auto object-cover" />
    </a>
  ) : null;
  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="blog-post-page">
      <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Tous les articles
      </Link>
      <span className="text-xs uppercase tracking-widest text-primary font-semibold">{post.category}</span>
      <h1 className="font-display font-bold text-4xl sm:text-5xl text-foreground tracking-tight mt-2 leading-tight">{post.title}</h1>
      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-3">
        <Calendar className="w-4 h-4" />
        {new Date(post.created_at).toLocaleDateString("fr-CA", { year: "numeric", month: "long", day: "numeric" })}
        <span>•</span>
        <span>par {post.author}</span>
      </div>
      {post.cover_url && (
        <div className="w-full aspect-[2/1] bg-secondary rounded-3xl mt-8 overflow-hidden flex items-center justify-center">
          <img src={post.cover_url} alt={post.title} className="w-full h-full object-contain" />
        </div>
      )}
      <div className="blog-content mt-8" data-testid="blog-content">
        {AdHoriz}
        {Array.isArray(post.blocks) && post.blocks.length > 0 ? (
          <BlogBlocks blocks={post.blocks} />
        ) : (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
            {post.content || ""}
          </ReactMarkdown>
        )}
        {AdRect}
      </div>
    </article>
  );
}
